import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Request builders for the SLR loan-standing-verification write endpoints.
 *
 * These hit root-namespaced REST routes (NOT `/api/v2`, NOT JSON:API) and
 * return plain JSON payloads, so callers cast the response `content` to the
 * relevant `tio-employee/types/slr-loan-standing-verification` shape. They
 * replace the hand-rolled `this.api.post` calls in
 * `tio-employee/app/services/slr-loan-standing-verification.ts`.
 */

const employeePlanAccountUrl = (path: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'employee-plan-accounts' },
    op: 'query',
    resourcePath: 'employee-plan-accounts',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/**
 * POST /employee-plan-accounts/:id/slr-loan-standing-verification/documents —
 * attach a signed verification document.
 *
 * `asset` is the `DigitalAssetDetailSignature` the caller already holds; it is
 * typed as `object` here because tio-data must not depend on tio-common (the
 * dependency arrow points the other way). It is serialized as `{ asset }`.
 */
export function uploadSlrLoanStandingDocumentRequest(
  employeePlanAccountId: string,
  asset: object
): RequestInfo {
  return {
    url: employeePlanAccountUrl(
      `${employeePlanAccountId}/slr-loan-standing-verification/documents`
    ),
    method: 'POST',
    body: JSON.stringify({ asset }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /employee-plan-accounts/:id/slr-loan-standing-verification/name-match-confirmation
 * — confirm (or reject) that the uploaded document's name matches the employee.
 */
export function confirmSlrLoanStandingNameMatchRequest(
  employeePlanAccountId: string,
  confirmed: boolean
): RequestInfo {
  return {
    url: employeePlanAccountUrl(
      `${employeePlanAccountId}/slr-loan-standing-verification/name-match-confirmation`
    ),
    method: 'POST',
    body: JSON.stringify({ confirmed }),
    cacheOptions: { [SkipCache]: true },
  };
}
